import { churchSupabase } from "./churchSupabaseClient";
import { isOikosChurchSalesHost } from "../../../core/utils/modeRouting";

const CHURCH_ORG_USER_SESSION_KEY = "oikos.church.orgUserSession";
const ORG_SESSION_DEBUG_PREFIX = "[ChurchOrgSession]";

function shouldLogOrgSessionDebug() {
  if (typeof window === "undefined") return process.env.NODE_ENV !== "production";

  return (
    process.env.NODE_ENV !== "production" ||
    window.localStorage.getItem("oikos.church.debugAuth") === "true"
  );
}

function orgSessionDebug(message, details = {}) {
  if (!shouldLogOrgSessionDebug()) return;
  console.info(ORG_SESSION_DEBUG_PREFIX, message, details);
}

function normalizeAccount(account = {}) {
  return {
    id: account.id || account.account_id || "",
    name: account.name || "",
    type: account.type || "church",
    invite_code: account.invite_code || "",
    church_code: account.church_code || "",
    logo_url: account.logo_url || account.logoUrl || "",
    logo_path: account.logo_path || account.logoPath || "",
    brand_color: account.brand_color || "",
    address_line_1: account.address_line_1 || account.addressLine1 || "",
    address_line_2: account.address_line_2 || account.addressLine2 || "",
    city: account.city || "",
    state_region: account.state_region || account.stateRegion || "",
    postal_code: account.postal_code || account.postalCode || "",
    country: account.country || "",
    integrations: account.integrations && typeof account.integrations === "object" ? account.integrations : {},
    website_enabled: account.website_enabled === true || account.websiteEnabled === true,
    website_settings: account.website_settings || account.websiteSettings || {},
  };
}

function normalizeOrgUser(user = {}) {
  const metadata = user.metadata && typeof user.metadata === "object" ? user.metadata : {};
  const legacyAccess = user.metadata?.access && typeof user.metadata.access === "object"
    ? user.metadata.access
    : {};
  const permissions = user.permissions && typeof user.permissions === "object"
    ? user.permissions
    : {
        appIds: Array.isArray(legacyAccess.appIds) ? legacyAccess.appIds : [],
        managementPageIds: Array.isArray(legacyAccess.managementPageIds)
          ? legacyAccess.managementPageIds
          : [],
      };

  return {
    id: user.id || user.org_user_id || "",
    account_id: user.account_id || "",
    display_name: user.display_name || "",
    username: user.username || "",
    email: user.email || "",
    role: user.role || "member",
    is_active: user.is_active !== false,
    familyId: user.familyId || user.family_id || metadata.familyId || metadata.assignedFamilyId || "",
    metadata,
    permissions: {
      appIds: Array.isArray(permissions.appIds) ? permissions.appIds : [],
      managementPageIds: Array.isArray(permissions.managementPageIds)
        ? permissions.managementPageIds
        : [],
    },
  };
}

function normalizeSession(payload = {}) {
  const account = normalizeAccount(payload.account || payload);
  const orgUser = normalizeOrgUser(payload.user || payload.org_user || payload.orgUser || payload);
  const sessionToken = payload.session_token || payload.sessionToken || "";

  if (!account.id || !orgUser.id || !sessionToken) {
    return null;
  }

  return {
    sessionToken,
    expiresAt: payload.expires_at || payload.expiresAt || "",
    account,
    orgUser,
  };
}

function isSessionStillFresh(session) {
  if (!session?.sessionToken) return false;
  if (!session.expiresAt) return true;

  const expiresAt = Date.parse(session.expiresAt);
  return Number.isNaN(expiresAt) || expiresAt > Date.now();
}

function describeSession(session) {
  return {
    hasSession: Boolean(session?.sessionToken),
    expiresAt: session?.expiresAt || "",
    accountId: String(session?.account?.id || "").slice(0, 8),
    orgUserId: String(session?.orgUser?.id || "").slice(0, 8),
    isFresh: isSessionStillFresh(session),
  };
}

function isMissingRpcError(error) {
  const message = String(error?.message || "").toLowerCase();
  return error?.code === "42883" || message.includes("could not find the function");
}

export function getStoredChurchOrgUserSession() {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.localStorage.getItem(CHURCH_ORG_USER_SESSION_KEY);
    const session = raw ? normalizeSession(JSON.parse(raw)) : null;
    orgSessionDebug("read stored org session", describeSession(session));
    return session;
  } catch (error) {
    orgSessionDebug("failed to read stored org session", {
      message: error?.message || String(error),
    });
    return null;
  }
}

export function storeChurchOrgUserSession(session) {
  if (typeof window === "undefined" || !session) return;

  window.localStorage.setItem(CHURCH_ORG_USER_SESSION_KEY, JSON.stringify(session));
  orgSessionDebug("stored org session", describeSession(session));
}

export function clearChurchOrgUserSession() {
  if (typeof window === "undefined") return;

  window.localStorage.removeItem(CHURCH_ORG_USER_SESSION_KEY);
  orgSessionDebug("cleared org session");
}

export async function lookupChurchOrg(orgCode) {
  orgSessionDebug("org lookup started", {
    hasOrgCode: Boolean(orgCode?.trim()),
    orgCodeLength: orgCode?.trim()?.length || 0,
  });

  const { data, error } = await churchSupabase.rpc("church_login_get_org", {
    p_org_code: orgCode.trim(),
  });

  if (error) {
    orgSessionDebug("org lookup failed", {
      code: error?.code || "",
      message: error?.message || String(error),
    });
    throw error;
  }

  const account = normalizeAccount(Array.isArray(data) ? data[0] : data);
  orgSessionDebug("org lookup finished", {
    found: Boolean(account.id),
    accountId: String(account.id || "").slice(0, 8),
    name: account.name,
  });
  return account.id ? account : null;
}

export async function lookupChurchOrgByDomain(hostname) {
  const cleanHostname = String(hostname || "").trim().toLowerCase();
  if (!cleanHostname || cleanHostname === "localhost" || cleanHostname.endsWith(".local")) {
    return null;
  }

  if (isOikosChurchSalesHost(cleanHostname)) {
    return null;
  }

  orgSessionDebug("domain org lookup started", {
    hostname: cleanHostname,
  });

  const { data, error } = await churchSupabase.rpc("church_domain_resolve", {
    p_domain: cleanHostname,
  });

  if (error) {
    orgSessionDebug("domain org lookup failed", {
      code: error?.code || "",
      message: error?.message || String(error),
    });
    throw error;
  }

  const account = normalizeAccount(data?.church || {});
  orgSessionDebug("domain org lookup finished", {
    found: Boolean(account.id),
    accountId: String(account.id || "").slice(0, 8),
    name: account.name,
  });
  return account.id ? account : null;
}

export async function loginChurchOrgUser(accountId, username, password) {
  orgSessionDebug("org user login started", {
    accountId: String(accountId || "").slice(0, 8),
    username: username?.trim() || "",
  });

  const { data, error } = await churchSupabase.rpc("church_org_user_login", {
    p_account_id: accountId,
    p_username: username.trim(),
    p_password: password,
  });

  if (error) {
    orgSessionDebug("org user login failed", {
      code: error?.code || "",
      message: error?.message || String(error),
      accountId: String(accountId || "").slice(0, 8),
      username: username?.trim() || "",
    });
    throw error;
  }

  const session = normalizeSession(Array.isArray(data) ? data[0] : data);
  if (session) {
    storeChurchOrgUserSession(session);
  }

  orgSessionDebug("org user login finished", describeSession(session));
  return session;
}

export async function requestChurchOrgUserJoin({
  accountId,
  displayName = "",
  username = "",
  password = "",
  email = "",
}) {
  orgSessionDebug("org user join request started", {
    accountId: String(accountId || "").slice(0, 8),
    username: username?.trim() || "",
  });

  const { data, error } = await churchSupabase.rpc("church_org_user_join_request", {
    p_account_id: accountId,
    p_display_name: displayName.trim(),
    p_username: username.trim(),
    p_password: password,
    p_email: email.trim(),
  });

  if (error) {
    orgSessionDebug("org user join request failed", {
      code: error?.code || "",
      message: error?.message || String(error),
      accountId: String(accountId || "").slice(0, 8),
      username: username?.trim() || "",
    });
    throw error;
  }

  const requestedUser = normalizeOrgUser(Array.isArray(data) ? data[0] : data);
  orgSessionDebug("org user join request finished", {
    accountId: String(requestedUser?.account_id || "").slice(0, 8),
    orgUserId: String(requestedUser?.id || "").slice(0, 8),
  });
  return requestedUser;
}

export async function refreshStoredChurchOrgUserSession() {
  orgSessionDebug("refresh stored org session started");
  const storedSession = getStoredChurchOrgUserSession();
  if (!storedSession?.sessionToken) {
    orgSessionDebug("refresh skipped: no stored org session");
    return null;
  }

  if (!isSessionStillFresh(storedSession)) {
    orgSessionDebug("refresh skipped: stored org session expired", describeSession(storedSession));
    clearChurchOrgUserSession();
    return null;
  }

  orgSessionDebug("org workspace RPC started", describeSession(storedSession));
  const { data, error } = await churchSupabase.rpc("church_org_user_workspace", {
    p_session_token: storedSession.sessionToken,
  });

  if (error) {
    if (isMissingRpcError(error)) {
      orgSessionDebug("org workspace RPC missing; using stored session", {
        ...describeSession(storedSession),
        message: error?.message || String(error),
      });
      return storedSession;
    }

    console.warn("Church org workspace refresh failed; using stored session.", error);
    orgSessionDebug("org workspace RPC failed; using stored session", {
      ...describeSession(storedSession),
      code: error?.code || "",
      message: error?.message || String(error),
    });
    return storedSession;
  }

  const session = normalizeSession(Array.isArray(data) ? data[0] : data);
  if (!session) {
    orgSessionDebug("org workspace RPC returned no usable session; clearing stored session");
    clearChurchOrgUserSession();
    return null;
  }

  storeChurchOrgUserSession(session);
  orgSessionDebug("refresh stored org session finished", describeSession(session));
  return session;
}

export function createChurchOrgAuthUser(session) {
  if (!session?.orgUser?.id) return null;

  return {
    id: session.orgUser.id,
    email: session.orgUser.email || session.orgUser.username || "",
    isChurchOrgUser: true,
    churchOrgSession: session,
    app_metadata: {
      provider: "church_org_user",
    },
  };
}

export function createChurchOrgProfile(session) {
  if (!session?.orgUser?.id) return null;

  return {
    id: session.orgUser.id,
    email: session.orgUser.email || "",
    full_name: session.orgUser.display_name || session.orgUser.username || "",
    is_approved: true,
    is_church_org_user: true,
    role: session.orgUser.role,
    account_id: session.account.id,
  };
}
